import React, { useState, useEffect } from 'react';
import { getUserPreferences, setHideInstallPrompt } from '../utils/supabaseDataManager';
import './InstallPrompt.css';

function InstallPrompt() {
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  useEffect(() => {
    const checkPreferences = async () => {
      // Check if already installed
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches
        || window.navigator.standalone
        || document.referrer.includes('android-app://');

      if (isStandalone) return;

      // Check database preference
      const prefs = await getUserPreferences();
      if (prefs.hideInstallPrompt) return;

      // Fallback to localStorage for backwards compatibility
      const dismissed = localStorage.getItem('installPromptDismissed');
      if (dismissed) return;

      // Show prompt after 3 seconds
      const timer = setTimeout(() => {
        setShowPrompt(true);
      }, 3000);

      return () => clearTimeout(timer);
    };

    checkPreferences();

    // Listen for beforeinstallprompt event (Android/Chrome)
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      // Android/Chrome - use native prompt
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      console.log(`User response: ${outcome}`);
      setDeferredPrompt(null);
      setShowPrompt(false);
    } else {
      // iOS - show instructions
      setShowPrompt(false);
      // The iOS instructions modal will show
    }
  };

  const handleDoNotShowAgain = async () => {
    setShowPrompt(false);
    // Save to database
    await setHideInstallPrompt(true);
    // Also save to localStorage for backwards compatibility
    localStorage.setItem('installPromptDismissed', 'true');
  };

  const handleMaybeLater = () => {
    setShowPrompt(false);
    // Don't set dismissed flag, will show again next visit
  };

  if (!showPrompt) return null;

  // Detect iOS
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

  return (
    <div className="install-prompt-overlay">
      <div className="install-prompt-card">
        <div className="install-prompt-header">
          <h3>📱 Add to Home Screen</h3>
          <button className="close-btn" onClick={handleMaybeLater}>×</button>
        </div>

        <div className="install-prompt-body">
          <p className="install-prompt-text">
            Get quick access to your workouts! Add this app to your home screen for a native app experience.
          </p>

          {isIOS ? (
            <div className="ios-instructions">
              <h4>📲 How to Install on iPhone:</h4>
              <ol>
                <li>Tap the <strong>Share button</strong> <span className="share-icon">⎙</span> at the bottom</li>
                <li>Scroll down and tap <strong>"Add to Home Screen"</strong></li>
                <li>Tap <strong>"Add"</strong> in the top right</li>
                <li>Open from your home screen! 🎉</li>
              </ol>
              <div className="ios-tip">
                💡 <strong>Tip:</strong> The Share button is at the bottom center of Safari (square with arrow pointing up)
              </div>
            </div>
          ) : (
            <div className="android-instructions">
              <button className="install-btn" onClick={handleInstall}>
                ⬇️ Install App
              </button>
              <p className="install-note">
                This will add a shortcut to your home screen
              </p>
            </div>
          )}
        </div>

        <div className="install-prompt-footer">
          <button className="btn-secondary" onClick={handleMaybeLater}>
            Maybe Later
          </button>
          <button className="btn-primary" onClick={handleDoNotShowAgain}>
            Do Not Show Again
          </button>
        </div>
      </div>
    </div>
  );
}

export default InstallPrompt;
